import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import { remove } from 'remove-accents';
import { PartnerService } from 'src/app/services/partner/partner.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { NgTemplateOutlet } from '@angular/common';
import { PayeeCopyButtonComponent } from '../../../../shared/payee-buttons/payee-copy-button/payee-copy-button.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { MatDialogClose } from '@angular/material/dialog';
import { PayeeDropzoneComponent } from '../../../../shared/payee-dropzone/payee-dropzone.component';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';

type LogoKind = 'formLogo' | 'smallLogo' | 'workflowNotificationLogo';

@Component({
  selector: 'app-create-partner-dialog',
  templateUrl: './create-partner-dialog.component.html',
  styleUrls: ['./create-partner-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    NgTemplateOutlet,
    PayeeCopyButtonComponent,
    PayeeButtonComponent,
    MatDialogClose,
    PayeeDropzoneComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
  ],
})
export class CreatePartnerDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private partnerService = inject(PartnerService);
  private snackbar = inject(MatSnackBar);
  private loading = false;
  private readonly destroy = new Subject<void>();

  readonly formGroup = this.fb.group({
    name: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    integration: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    origin: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
  });

  readonly logoAccept =
    'image/png,image/jpeg,image/gif,image/webp,image/svg+xml,.png,.jpg,.jpeg,.gif,.webp,.svg';

  partnerToken?: string;
  formLogo: File | null = null;
  smallLogo: File | null = null;
  workflowNotificationLogo: File | null = null;
  formLogoPreview: string | null = null;
  smallLogoPreview: string | null = null;
  workflowNotificationLogoPreview: string | null = null;

  get name() {
    return this.formGroup.controls.name;
  }
  get integration() {
    return this.formGroup.controls.integration;
  }
  get origin() {
    return this.formGroup.controls.origin;
  }

  ngOnInit(): void {
    this.name.valueChanges
      .pipe(takeUntil(this.destroy), distinctUntilChanged())
      .subscribe((name) => {
        const noAccents = remove(name);

        this.integration.patchValue(
          noAccents
            .toLowerCase()
            .trim()
            .replace(/[.]/g, '')
            .replace(/[-]/g, '_')
            .replace(/\s+/g, '-'),
        );

        this.origin.patchValue(
          'PLUGIN_' +
            noAccents
              .toUpperCase()
              .trim()
              .replace(/[.]/g, '')
              .replace(/[-]/g, '_')
              .replace(/\s+/g, '-'),
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    for (const preview of [
      this.formLogoPreview,
      this.smallLogoPreview,
      this.workflowNotificationLogoPreview,
    ]) {
      if (preview?.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    }
  }

  filesDropped(kind: LogoKind, files: FileList): void {
    this.setLogo(kind, files[0] ?? null);
  }

  removeLogo(kind: LogoKind): void {
    this.setLogo(kind, null);
  }

  private setLogo(kind: LogoKind, file: File | null): void {
    const previewKey = `${kind}Preview` as
      | 'formLogoPreview'
      | 'smallLogoPreview'
      | 'workflowNotificationLogoPreview';

    const previousUrl = this[previewKey];
    if (previousUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previousUrl);
    }

    this[kind] = file;
    this[previewKey] = file ? URL.createObjectURL(file) : null;
  }

  async submit() {
    if (this.formGroup.invalid || this.loading) {
      this.name.markAsDirty();
      this.integration.markAsDirty();
      this.origin.markAsDirty();

      return;
    }

    try {
      this.loading = true;
      const { partner } = await this.partnerService.createPartner({
        ...this.formGroup.getRawValue(),
        form_logo: this.formLogo,
        small_logo: this.smallLogo,
        workflow_notification_logo: this.workflowNotificationLogo,
      });

      this.partnerToken = partner.api_key;
    } catch (error) {
      this.name.markAsDirty();
      this.integration.markAsDirty();
      this.origin.markAsDirty();

      if (error instanceof AxiosError) {
        if (error.response?.status === 422) {
          const errors: undefined | Record<string, string[]> =
            error.response?.data?.errors;
          if (errors) {
            Object.entries(errors).forEach(([key, errors]) => {
              const control = this.formGroup.get(key);
              if (control) {
                control.setErrors({
                  unknown: errors[0],
                });
              } else {
                this.formGroup.setErrors({
                  unknown: errors[0],
                });
              }
            });
            return;
          }
        }
      }
      this.snackbar.open('Ismeretlen hiba!', 'OK', { duration: 5000 });
      console.error('Unknown error while creating partner!', error);
    } finally {
      this.loading = false;
    }
  }
}
